import React, { useState } from 'react';
import styled from 'styled-components';
import { space, width } from 'styled-system';
import { yup } from 'shared/core/yup';
import moment from 'moment';
import _ from 'lodash';
import { useApolloClient } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Box } from '@dutchie/common/components/rebass-compat';

import { useCustomTermsUrl } from 'src/utils/misc-utils';
import useCart from 'hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useErnie from 'shared/hooks/use-ernie';
import useGeoinfo from 'hooks/use-geoinfo';
import useTranslation from 'hooks/use-translation';
import useUI from 'hooks/use-ui';
import useUser from 'hooks/use-user';

import { Button } from 'components/core';
import { consumerSignupV2 } from 'src/state/actions/users';
import { tracker } from 'utils/analytics';
import { useTurnstile } from 'src/components/turnstile';
import { TURNSTILE_ACTIONS } from 'shared/constants/turnstile';
import { LoginOptionsContainer } from '../components';
import { FormTextInput, FormFormattedInput } from '../../form-inputs';
import Form from '../form';
import { emailRegEx, birthdayRegEx } from '../form-utils';
import MedicalCardForm, { CO } from './medical-card-form';

const signUpFormValidation = (t, showMedicalCardForm) => {
  const medicalCardVerification = showMedicalCardForm
    ? {
        medicalCardNumber: yup.string().when([`state`], {
          is: (state) => state === CO,
          then: yup.string(),
          otherwise: yup.string().required(t('form.medicalCardNumber', 'medical card number is required')),
        }),
        expirationDate: yup
          .string()
          .beforeToday(`MM/DD/YYYY`, `medical card is expired`)
          .required(t('form.expirationDate', 'expiration date is required')),
        state: yup.string().required(t('form.state', 'state is required')),
      }
    : {};

  return yup.object().shape({
    firstName: yup.string().required(t('form.firstNameRequired', 'first name is required')),
    lastName: yup.string().required(t('form.lastNameRequired', 'last name is required')),
    email: yup
      .string()
      .required(t('form.emailRequired', 'email is required'))
      .matches(emailRegEx, t('form.validEmail', 'please enter a valid email address')),
    password: yup.string().required(t('form.passwordRequired', 'password is required')).min(8),
    phoneNumber: yup
      .string()
      .required(t('form.phoneNumberRequired', 'phone number is required'))
      .phone(t('form.validPhoneNumber', 'please enter a valid phone number')),
    birthday: yup
      .string()
      .required(t('form.birthdayRequired', 'birthday is required'))
      .isLegalAge(`MM/DD/YYYY`, t('form.isLegalAge', 'You must be at least 18 years old to use dutchie'))
      .matches(birthdayRegEx, t('form.validBirthday', 'please enter a valid birthday')),
    ...medicalCardVerification,
  });
};
/**
 * @typedef {Object} SignUpFormProps
 * @property {() => void | null} [onClose] - Optional callback function when form is closed
 * @property {string} [analyticsEventLabel] - Optional label for analytics tracking
 * @property {boolean} [displayLoginLink=true] - Whether to show the login link, defaults to true
 * @property {string | null} [publicContextSessionId] - Optional public context session id
 * @property {string} [submitButtonLabel] - Optional custom label for the submit button
 */

/**
 * @param {SignUpFormProps} props
 * @returns {JSX.Element}
 */
const SignUpForm = ({
  onClose = null,
  analyticsEventLabel,
  displayLoginLink = true,
  publicContextSessionId = null,
  submitButtonLabel,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showMedicalCardForm, setShowMedicalCardForm] = useState(false);
  const UI = useUI();
  const { isDutchieMain, isEmbedded, isStoreFront } = UI;
  const Cart = useCart();
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const { dispensary } = useDispensary();
  const showErnie = useErnie();
  const User = useUser();
  const [geoinfo] = useGeoinfo();
  const country = geoinfo?.country;
  const flags = useFlags();
  const isTurnstileEnabled = flags['growth.ecomm.signup-turnstile-enable.rollout'] ?? false;
  const customTermsUrl = useCustomTermsUrl();

  const { TurnstileComponent, executeAndWaitForToken } = useTurnstile({
    size: 'flexible',
    action: TURNSTILE_ACTIONS.SIGNUP,
  });

  const ageCheck = (e) => {
    if (!e || e.currentTarget?.contains(e.relatedTarget)) {
      return;
    }
    const birthday = e.target.value;
    const formattedBirthday = moment(birthday, `MM/DD/YYYY`, true);

    if (!formattedBirthday.isValid()) {
      return;
    }

    const yearDiff = moment().diff(formattedBirthday, `years`);

    if (!showMedicalCardForm && yearDiff >= 18 && yearDiff < 21 && country !== 'CA') {
      setShowMedicalCardForm(true);
    }

    if (moment().diff(formattedBirthday, `years`) < 18) {
      showErnie(t('signUp.ageGate', 'You must be 18 years of age or older to use dutchie'), `danger`);
      setShowMedicalCardForm(false);
    }

    if (showMedicalCardForm && yearDiff >= 21) {
      setShowMedicalCardForm(false);
    }
  };

  const href = !isDutchieMain && customTermsUrl ? customTermsUrl : `/terms`;

  const trackAccountSignup = (activeDispensary, userId, label) => {
    tracker.setContext({ activeDispensary });
    tracker.accountLogin({ newAccount: true, customerId: userId, analyticsEventLabel: label });
  };

  const handleFormSubmit = async ({
    firstName,
    lastName,
    email,
    password,
    phoneNumber,
    birthday,
    medicalCardNumber,
    expirationDate,
    state,
  }) => {
    setIsLoading(true);
    try {
      let turnstileToken = '';
      if (isTurnstileEnabled) {
        try {
          turnstileToken = await executeAndWaitForToken();
        } catch (error) {
          console.error(error);
          showErnie(t('signUp.turnstileError', 'Error validating your request. Please try again.'), 'danger');
          setIsLoading(false);
          return;
        }
      }

      const profile = {
        email,
        password,
        profile: {
          firstName: _.trim(firstName),
          lastName: _.trim(lastName),
          phone: phoneNumber,
          birthday,
          textNotifications: true,
          emailNotifications: true,
          medicalCard: showMedicalCardForm
            ? {
                number: state !== CO ? medicalCardNumber : '',
                expirationDate,
                state,
              }
            : null,
        },
      };

      const userCreationData = {
        dispensaryId: dispensary?.id,
        embedded: isEmbedded || isStoreFront,
      };
      const { success, error, user } = await consumerSignupV2({
        apolloClient,
        User,
        Cart,
        UI,
        profile,
        userCreationData,
        turnstileToken,
        publicContextSessionId,
      });
      if (success) {
        trackAccountSignup(dispensary, user._id, analyticsEventLabel);
        const successMessage = t('signUp.success', 'Success! Your account has been created.');

        showErnie(successMessage, `success`);
        onClose?.();
      } else if (error.message) {
        showErnie(error.message, `danger`);
      } else {
        const errorMessage = t('signUp.fail', 'We are unable to create your account. Please contact support.');
        showErnie(errorMessage, `danger`);
      }
    } catch (err) {
      const errorMessage = err.message;
      console.error(errorMessage);
      showErnie(errorMessage, `danger`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form onSubmit={handleFormSubmit} mode='onSubmit' validationSchema={signUpFormValidation(t, showMedicalCardForm)}>
      <FormContainer>
        <InlineContainer>
          <InputContainer width='50%' mr={15}>
            <FormTextInput
              name='firstName'
              label={t('signUp.firstName', 'First Name')}
              variant='filled'
              autoComplete='given-name'
              data-testid='sign-up-firstname'
              required
            />
          </InputContainer>

          <InputContainer width='50%'>
            <FormTextInput
              name='lastName'
              label={t('signUp.lastName', 'Last Name')}
              variant='filled'
              autoComplete='family-name'
              data-testid='sign-up-lastname'
              required
            />
          </InputContainer>
        </InlineContainer>

        <InputContainer mt={18}>
          <FormFormattedInput
            format='phone'
            name='phoneNumber'
            label={t('signUp.phone', 'Mobile Phone Number')}
            variant='filled'
            autoComplete='tel'
            data-testid='sign-up-phone'
            required
          />
        </InputContainer>

        <InputContainer onBlur={ageCheck} mt={18}>
          <FormFormattedInput
            format='date'
            name='birthday'
            label={t('signUp.birthday', 'Birthday')}
            variant='filled'
            autoComplete='bday'
            data-testid='sign-up-birthday'
            required
          />
        </InputContainer>

        <InputContainer mt={18}>
          <FormTextInput
            name='email'
            label={t('signUp.email', 'Email Address')}
            variant='filled'
            autoComplete='email'
            type='email'
            data-testid='sign-up-email'
            required
          />
        </InputContainer>

        <InputContainer mt={18}>
          <FormTextInput
            name='password'
            label={t('signUp.password', 'Password')}
            type='password'
            variant='filled'
            autoComplete='new-password'
            data-testid='sign-up-password'
            required
          />
        </InputContainer>
      </FormContainer>

      {showMedicalCardForm && <MedicalCardForm />}
      {isTurnstileEnabled && (
        <Box mt='20px'>
          <TurnstileComponent />
        </Box>
      )}

      <LoginOptionsContainer>
        <SecondaryOptionTextContainer>
          {displayLoginLink && (
            <Terms>
              By creating an account, you agree to the{' '}
              <a data-testid='terms-link' href={href} target='_blank' rel='noopener noreferrer'>
                Terms and Conditions
              </a>
            </Terms>
          )}
        </SecondaryOptionTextContainer>

        <StyledButton data-testid='sign-up-submit-button' type='submit' variant='flora' loading={isLoading}>
          {submitButtonLabel ?? 'Sign Up'}
        </StyledButton>
      </LoginOptionsContainer>
    </Form>
  );
};

export default SignUpForm;

const SecondaryOptionTextContainer = styled.div`
  display: flex;
  margin-right: 16px;
`;

const InlineContainer = styled.div`
  display: flex;

  ${space}
`;

const FormContainer = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
`;

export const InputContainer = styled.div`
  ${width}
  ${space}
`;

export const StyledButton = styled(Button)`
  background-color: var(--dct--customized-colors-buttonsLinks);
  padding: 12px 22px;
`;

const Terms = styled.div`
  color: var(--dcm-modal-text-color, #828a8f);
  font-size: 12px;
  line-height: ${16 / 12};
  text-align: left;

  a {
    color: var(--dct--customized-colors-buttonsLinks);

    &:hover {
      text-decoration: underline;
    }
  }
`;
